<template>
  <div style="padding: 20px; min-height: 630px; background-color: rgba(221, 225, 230, 1); border: solid 1px #ccc">
    <div class="metric-filters">
      <span class="title_bi metric-filters-title">Estado Financiero</span>
      <div class="metric-filter metric-filter-empresa">
        <v-autocomplete v-model="emitters_selected" :items="emitters" item-text="name" item-value="id" label="Empresas" multiple small-chips dense hide-details autocomplete="off" class="pt-0 mt-0" @change="onEmittersChange">
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" small class="ma-0 mr-1">{{ item.name }}</v-chip>
            <span v-if="index === 2" class="grey--text text-caption">(+{{ emitters_selected.length - 2 }})</span>
          </template>
        </v-autocomplete>
      </div>
      <v-btn-toggle v-model="period_type" mandatory dense class="metric-filter-toggle" @change="load">
        <v-btn small value="month">Mes</v-btn>
        <v-btn small value="year">Año</v-btn>
      </v-btn-toggle>
      <div v-if="period_type === 'month'" class="metric-filter metric-filter-mes">
        <v-combobox v-model="mes_selected" autocomplete="off" item-text="name" item-value="id" :items="months" label="Mes" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
      <div class="metric-filter metric-filter-anio">
        <v-combobox v-model="year" :items="years" label="Año" class="pt-0 mt-0" dense hide-details @change="load"></v-combobox>
      </div>
    </div>

    <p v-if="emitters_selected.length > 1" class="caption grey--text mt-n3 mb-3">
      Consolidado de {{ emitters_selected.length }} empresas. Se excluyen las transferencias internas; las operaciones entre empresas propias no se eliminan.
    </p>
    <p v-if="emitters.length === 0 && !emitters_error && !loading" class="caption mt-4">No hay empresas emisoras configuradas.</p>

    <!-- La grilla con titulos se ve siempre; mientras carga, cada valor muestra su skeleton. -->
    <template v-if="loading || data">
      <!-- Indicadores -->
      <div class="row">
        <div class="col-md-3" v-for="kpi in kpis" :key="kpi.title">
          <v-card class="metric-card metric-kpi-card">
            <p class="title_bi mb-0">{{ kpi.title }}</p>
            <p class="caption mb-0">{{ kpi.caption }}</p>
            <div class="metric-kpi">
              <template v-if="ready">
                <p class="text-h6 font-weight-bold mb-1" :style="kpi.color ? 'color:' + kpi.color : ''">{{ money(kpi.amount) }}</p>
              </template>
              <v-skeleton-loader v-else type="heading" width="170" class="metric-skeleton-value mb-2"></v-skeleton-loader>
              <router-link v-if="kpi.link" :to="kpi.link" class="caption">Ver detalle</router-link>
              <span v-else class="caption">&nbsp;</span>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Ingresos vs Egresos -->
      <div class="row">
        <div class="col-md-8">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Ingresos vs Egresos</p>
              <p class="caption text-slate-500 mb-0">Percibido (con IVA), según Resultados por Tesorería</p>
            </div>
            <div class="metric-chips">
              <template v-if="ready">
                <v-chip small outlined color="#388E3C">Ingresos: {{ money(ie.total_ingresos) }}</v-chip>
                <v-chip small outlined color="#E53935">Egresos: {{ money(ie.total_egresos) }}</v-chip>
              </template>
              <template v-else>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
              </template>
              <router-link :to="'/' + Constant.SECTION_REPORT_RESULTADOS" class="caption">Ver reporte</router-link>
            </div>
            <div class="metric-card-body pt-0">
              <apexchart v-if="ready" type="bar" :height="chartHeight" :options="optionsIngresosEgresos" :series="seriesIngresosEgresos"></apexchart>
              <div v-else class="metric-skeleton-chart" :style="{ height: chartHeight - 30 + 'px' }">
                <div v-for="(bar, i) in skeletonBars" :key="i" class="metric-skeleton-group">
                  <div class="metric-skeleton-bar" :style="{ height: bar[0] + '%' }"></div>
                  <div class="metric-skeleton-bar" :style="{ height: bar[1] + '%' }"></div>
                </div>
              </div>
            </div>
          </v-card>
        </div>
        <div class="col-md-4 d-flex flex-column metric-stack">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Resultado financiero</p>
              <p class="caption text-slate-500 mb-0">Ingresos - Egresos</p>
            </div>
            <div class="metric-card-body metric-kpi">
              <p v-if="ready" class="text-h5 font-weight-bold mb-0" :style="'color:' + colorResultado">{{ money(ie.resultado) }}</p>
              <v-skeleton-loader v-else type="heading" width="200" class="metric-skeleton-value"></v-skeleton-loader>
            </div>
          </v-card>
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Ratio de cobertura</p>
              <p class="caption text-slate-500 mb-0">Ingresos / Egresos</p>
            </div>
            <div class="metric-card-body metric-kpi">
              <p v-if="ready" class="text-h5 font-weight-bold mb-0" :style="'color:' + colorResultado">{{ ratioCobertura }}</p>
              <v-skeleton-loader v-else type="heading" width="80" class="metric-skeleton-value"></v-skeleton-loader>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Principales conceptos -->
      <div class="row">
        <div class="col-md-6" v-for="top in topConceptos" :key="top.title">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">{{ top.title }}</p>
              <p class="caption text-slate-500 mb-0">Conceptos de mayor importe del período</p>
            </div>
            <div class="metric-card-body">
              <template v-if="ready">
                <p v-if="top.items.length === 0" class="caption mb-0">Sin movimientos en el período.</p>
                <div v-for="item in top.items" :key="item.id" class="metric-line">
                  <span class="metric-line-name">{{ item.name }}</span>
                  <span class="metric-line-amount">{{ money(item.amount) }}</span>
                </div>
              </template>
              <div v-else v-for="n in 5" :key="n" class="metric-line">
                <v-skeleton-loader type="text" width="50%" class="metric-skeleton-text"></v-skeleton-loader>
                <v-skeleton-loader type="text" width="25%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
        </div>
      </div>

      <v-divider></v-divider>
      <p class="title_bi mb-0 mt-2">Situación actual</p>
      <p class="caption mb-3">Fondos disponibles, cheques a {{ diasCheques }} días y deuda pendiente al día de hoy</p>

      <!-- Liquidez y proyeccion de cheques -->
      <div class="row">
        <div class="col-md-4">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Liquidez</p>
              <p class="caption text-slate-500 mb-0">Saldo de las cuentas que representan fondos</p>
            </div>
            <div class="metric-card-body d-flex flex-column">
              <template v-if="ready">
                <v-expansion-panels accordion flat class="metric-panels">
                  <v-expansion-panel v-for="tipo in data.liquidez.tipos" :key="tipo.id">
                    <v-expansion-panel-header class="px-0 py-0">
                      <div class="metric-line mb-0 mr-2">
                        <span class="metric-line-name">{{ tipo.name }}</span>
                        <span class="metric-line-amount" :style="tipo.total < 0 ? 'color:#E53935' : ''">{{ money(tipo.total) }}</span>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div v-for="cuenta in cuentasConSaldo(tipo)" :key="cuenta.id" class="d-flex justify-space-between caption py-1">
                        <span>{{ cuenta.name }}<span v-if="emitters_selected.length > 1" class="grey--text"> · {{ cuenta.emitter_name }}</span></span>
                        <span :style="cuenta.amount < 0 ? 'color:#E53935' : ''">{{ money(cuenta.amount) }}</span>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>
              <div v-else v-for="n in 4" :key="n" class="metric-line">
                <v-skeleton-loader type="text" width="40%" class="metric-skeleton-text"></v-skeleton-loader>
                <v-skeleton-loader type="text" width="30%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
              <div class="metric-line metric-line-total mt-auto">
                <span class="metric-line-name font-weight-bold">Total</span>
                <span v-if="ready" class="metric-line-amount">{{ money(data.liquidez.total) }}</span>
                <v-skeleton-loader v-else type="text" width="30%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
        </div>
        <div class="col-md-8">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">Cheques - Proyección a {{ diasCheques }} días</p>
              <p class="caption text-slate-500 mb-0">Terceros a acreditar y propios a debitar. "Anterior" acumula los pendientes vencidos.</p>
            </div>
            <div class="metric-chips">
              <template v-if="ready">
                <v-chip small outlined color="#388E3C">A cobrar: {{ money(cheques.total_ingresos) }}</v-chip>
                <v-chip small outlined color="#E53935">A pagar: {{ money(cheques.total_egresos) }}</v-chip>
                <v-chip v-if="cheques.sin_imputar.cantidad > 0" small outlined color="orange">
                  {{ cheques.sin_imputar.cantidad }} sin imputar: {{ money(cheques.sin_imputar.monto) }}
                </v-chip>
              </template>
              <template v-else>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
              </template>
              <router-link :to="'/' + Constant.SECTION_REPORT_CASHFLOW_CHEQUES" class="caption">Ver Cash Flow</router-link>
            </div>
            <div class="metric-card-body pt-0">
              <apexchart v-if="ready" type="line" :height="chartHeight" :options="optionsCheques" :series="seriesCheques"></apexchart>
              <div v-else class="metric-skeleton-chart" :style="{ height: chartHeight - 30 + 'px' }">
                <div v-for="(bar, i) in skeletonBars" :key="i" class="metric-skeleton-group">
                  <div class="metric-skeleton-bar" :style="{ height: bar[0] + '%' }"></div>
                  <div class="metric-skeleton-bar" :style="{ height: bar[1] + '%' }"></div>
                </div>
              </div>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Antiguedad de deuda -->
      <div class="row">
        <div class="col-md-6" v-for="aging in agings" :key="aging.title">
          <v-card class="metric-card">
            <div class="metric-card-header container-title-box">
              <p class="title_bi mb-0">{{ aging.title }}</p>
              <p class="caption text-slate-500 mb-0">Antigüedad por vencimiento del comprobante. Incluye todas las empresas.</p>
            </div>
            <div class="metric-chips">
              <template v-if="ready">
                <v-chip small outlined>Total: {{ money(aging.data.total) }}</v-chip>
                <v-chip small outlined color="#E53935">Vencido: {{ money(aging.data.vencido) }}</v-chip>
              </template>
              <template v-else>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
                <v-skeleton-loader type="chip"></v-skeleton-loader>
              </template>
              <router-link :to="aging.link" class="caption">Ver detalle</router-link>
            </div>
            <div class="metric-card-body pt-0">
              <apexchart v-if="ready" type="bar" :height="chartHeight" :options="optionsAging" :series="[{ name: aging.title, data: aging.data.tramos.map((t) => t.amount) }]"></apexchart>
              <div v-else class="metric-skeleton-hchart" :style="{ height: chartHeight - 30 + 'px' }">
                <div v-for="(width, i) in skeletonAging" :key="i" class="metric-skeleton-row">
                  <v-skeleton-loader type="text" width="110" class="metric-skeleton-text"></v-skeleton-loader>
                  <div class="metric-skeleton-hbar" :style="{ width: width + '%' }"></div>
                </div>
              </div>
              <p class="caption font-weight-bold mb-2">{{ aging.topTitle }}</p>
              <template v-if="ready">
                <p v-if="aging.data.top.length === 0" class="caption mb-0">Sin saldos pendientes.</p>
                <div v-for="item in aging.data.top" :key="item.id" class="metric-line">
                  <span class="metric-line-name">{{ item.name }}</span>
                  <span class="metric-line-amount">
                    {{ money(item.amount) }}
                    <span class="caption d-block" :style="item.vencido > 0 ? 'color:#E53935' : 'color:#9e9e9e'">vencido {{ money(item.vencido) }}</span>
                  </span>
                </div>
              </template>
              <div v-else v-for="n in 5" :key="n" class="metric-line">
                <v-skeleton-loader type="text" width="45%" class="metric-skeleton-text"></v-skeleton-loader>
                <v-skeleton-loader type="text" width="25%" class="metric-skeleton-text"></v-skeleton-loader>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { metricsService } from "@/libs/ws/metricsService.js";
import { emmiterService } from "@/libs/ws/emmiterService";
import { formatUtils } from "@/libs/utils/formatUtils";
import Constant from "@/constants/sections";

const COLOR_INGRESO = "#388E3C";
const COLOR_EGRESO = "#E53935";
const COLOR_SALDO = "#1E88E5";

/** Importe abreviado para los ejes: 1.500.000 -> "1,5 M". */
function shortMoney(val) {
  const abs = Math.abs(val);
  if (abs >= 1e9) return (val / 1e9).toFixed(1).replace(".", ",") + " MM";
  if (abs >= 1e6) return (val / 1e6).toFixed(1).replace(".", ",") + " M";
  if (abs >= 1e3) return (val / 1e3).toFixed(0) + " K";
  return Math.round(val).toString();
}

const MONTHS = [
  { id: 1, name: "Enero" },
  { id: 2, name: "Febrero" },
  { id: 3, name: "Marzo" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Mayo" },
  { id: 6, name: "Junio" },
  { id: 7, name: "Julio" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Septiembre" },
  { id: 10, name: "Octubre" },
  { id: 11, name: "Noviembre" },
  { id: 12, name: "Diciembre" },
];

export default {
  data() {
    const year = new Date().getFullYear();
    return {
      Constant: Constant,
      // Arranca en true: el skeleton se ve desde que se entra, mientras se cargan las empresas.
      loading: true,
      emitters_selected: [],
      emitters_prev: [],
      emitters_error: false,
      loadSeq: 0,
      emitters: [],
      period_type: "month",
      months: MONTHS,
      mes_selected: MONTHS[new Date().getMonth()],
      year: year,
      years: Array.from({ length: 6 }, (_, i) => year - i),
      diasCheques: 30,
      chartHeight: 280,
      // Alturas (%) de las barras ingreso / egreso del skeleton de los graficos.
      skeletonBars: [[55, 40], [70, 50], [45, 60], [80, 55], [60, 35], [75, 65], [50, 45], [85, 60], [65, 50], [40, 30], [70, 55], [60, 45]],
      // Ancho (%) de las barras del skeleton de antiguedad de deuda, una por tramo.
      skeletonAging: [70, 40, 55, 85, 35, 15, 25],
      data: null,
    };
  },
  mounted() {
    emmiterService.filter({ enabled: true }).then((response) => {
      if (response.status === false) {
          this.emitters_error = true;
          this.loading = false;
          this.$toast.error(response.msg || "No se pudieron cargar las empresas. Intentá nuevamente.", "Error");
          return;
        }
        this.emitters = response.result;
      // Por defecto se elige la primera empresa.
      if (this.emitters.length > 0) {
        this.emitters_selected = [this.emitters[0].id];
        this.emitters_prev = [...this.emitters_selected];
        this.load();
      } else {
        this.loading = false;
      }
    }).catch(() => {
      this.emitters_error = true;
      this.loading = false;
      this.$toast.error("No se pudieron cargar las empresas. Intentá nuevamente.", "Error");
    });
  },
  computed: {
    // Los titulos de las cards se muestran siempre; los valores solo cuando llegaron los datos.
    ready() {
      return !this.loading && this.data != null;
    },
    ie() {
      return this.data.ingresos_egresos;
    },
    cheques() {
      return this.data.proyeccion_cheques;
    },
    kpis() {
      const k = this.ready ? this.data.kpis : {};
      return [
        { title: "Disponibilidad", caption: "Cajas, bancos y cheques en cartera", amount: k.disponibilidad, color: k.disponibilidad < 0 ? COLOR_EGRESO : null },
        {
          title: "Cheques netos",
          caption: "A cobrar - a pagar, próximos " + this.diasCheques + " días",
          amount: k.cheques_neto,
          color: k.cheques_neto < 0 ? COLOR_EGRESO : COLOR_INGRESO,
          link: "/" + Constant.SECTION_REPORT_CASHFLOW_CHEQUES,
        },
        { title: "Cobranzas vencidas", caption: "Comprobantes vencidos a cobrar", amount: k.vencido_a_cobrar, link: "/" + Constant.SECTION_REPORTE_CLIENT_PENDING_CHARGE },
        { title: "Pagos vencidos", caption: "Comprobantes vencidos a pagar", amount: k.vencido_a_pagar, color: k.vencido_a_pagar > 0 ? COLOR_EGRESO : null, link: "/" + Constant.SECTION_REPORTE_SUPPLIER_PENDING_SPENT },
      ];
    },
    colorResultado() {
      return this.ie.resultado < 0 ? COLOR_EGRESO : COLOR_INGRESO;
    },
    ratioCobertura() {
      return this.ie.total_egresos === 0 ? "-" : (this.ie.total_ingresos / this.ie.total_egresos).toFixed(2);
    },
    topConceptos() {
      return [
        { title: "Principales ingresos", items: this.ready ? this.ie.top_ingresos : [] },
        { title: "Principales egresos", items: this.ready ? this.ie.top_egresos : [] },
      ];
    },
    agings() {
      return [
        { title: "Cuentas a cobrar", topTitle: "Clientes con mayor saldo", data: this.ready ? this.data.cobrar : null, link: "/" + Constant.SECTION_REPORTE_CLIENT_PENDING_CHARGE },
        { title: "Cuentas a pagar", topTitle: "Proveedores con mayor saldo", data: this.ready ? this.data.pagar : null, link: "/" + Constant.SECTION_REPORTE_SUPPLIER_PENDING_SPENT },
      ];
    },
    seriesIngresosEgresos() {
      return [
        { name: "Ingresos", data: this.ie.ingresos },
        { name: "Egresos", data: this.ie.egresos },
      ];
    },
    optionsIngresosEgresos() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        dataLabels: { enabled: false },
        colors: [COLOR_INGRESO, COLOR_EGRESO],
        legend: { horizontalAlign: "left" },
        xaxis: { categories: this.ie.columnas.map((c) => c.name) },
        yaxis: { labels: { formatter: shortMoney } },
        tooltip: { y: { formatter: this.money } },
      };
    },
    seriesCheques() {
      return [
        { name: "A cobrar", type: "column", data: this.cheques.ingresos },
        { name: "A pagar", type: "column", data: this.cheques.egresos },
        { name: "Saldo acumulado", type: "line", data: this.cheques.saldo },
      ];
    },
    optionsCheques() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        dataLabels: { enabled: false },
        stroke: { width: [0, 0, 2] },
        colors: [COLOR_INGRESO, COLOR_EGRESO, COLOR_SALDO],
        legend: { horizontalAlign: "left" },
        labels: this.cheques.columnas.map((c) => c.name),
        xaxis: { tickAmount: 10 },
        yaxis: { labels: { formatter: shortMoney } },
        tooltip: { y: { formatter: this.money } },
      };
    },
    optionsAging() {
      const tramos = this.data.cobrar.tramos;
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, distributed: true } },
        dataLabels: { enabled: false },
        legend: { show: false },
        colors: ["#B71C1C", "#E53935", "#EF9A9A", "#90CAF9", "#1E88E5", "#BDBDBD", "#9E9E9E"],
        xaxis: { categories: tramos.map((t) => t.name), labels: { formatter: shortMoney } },
        tooltip: { y: { formatter: this.money } },
      };
    },
  },
  methods: {
    money(value) {
      return formatUtils.formatPrice(value);
    },
    cuentasConSaldo(tipo) {
      return tipo.cuentas.filter((c) => c.amount !== 0);
    },
    onEmittersChange() {
      // Sin empresas no hay nada que consolidar: se repone la selección anterior.
      if (this.emitters_selected.length === 0) {
        this.emitters_selected = [...this.emitters_prev];
        return;
      }
      this.emitters_prev = [...this.emitters_selected];
      this.load();
    },
    load() {
      if (this.emitters_selected.length === 0) {
        this.loadSeq++;
        this.data = null;
        this.loading = false;
        return;
      }
      if (!this.year || (this.period_type === "month" && !(this.mes_selected && this.mes_selected.id))) {
        this.loadSeq++;
        this.loading = false;
        return;
      }

      const param = { emitters: this.emitters_selected, period_type: this.period_type };
      if (this.period_type === "month") param.month = this.year + "-" + String(this.mes_selected.id).padStart(2, "0");
      else param.year = this.year;

      // Solo la respuesta de la última carga pisa los datos y apaga el loading.
      const seq = ++this.loadSeq;
      this.loading = true;
      metricsService
        .getEstadoFinanciero(param)
        .then((response) => {
          if (seq !== this.loadSeq) return;
          if (response.status === false) {
            this.$toast.error(response.msg, "Error");
            this.data = null;
            return;
          }
          this.data = response.result;
        })
        .catch((error) => {
          if (seq !== this.loadSeq) return;
          console.log(error);
          this.$toast.error("No se pudo cargar el estado financiero. Intentá nuevamente.", "Error");
          this.data = null;
        })
        .finally(() => {
          if (seq === this.loadSeq) this.loading = false;
        });
    },
  },
};
</script>
